import { useUsers } from '@/domains/users';
import { Button } from '@/shared/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface UserComboboxProps {
  value: number | undefined;
  onSelect: (userId: number | undefined) => void;
}

export function UserCombobox({ value, onSelect }: UserComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  function handleOpenChange(isOpen: boolean) {
    setOpen(isOpen);
    if (isOpen) {
      setSearch('');
      setDebouncedSearch('');
    }
  }

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timerRef.current);
  }, [search]);

  const trimmedSearch = debouncedSearch.trim();
  const { data, isLoading: isLoadingUsers } = useUsers(
    { search: trimmedSearch || undefined },
    open,
  );
  const users = data?.users ?? [];

  function handleSelect(userId: number, label: string) {
    onSelect(userId);
    setSelectedLabel(label);
    setOpen(false);
    setSearch('');
  }

  function handleClear() {
    onSelect(undefined);
    setSelectedLabel('');
    setSearch('');
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[200px] justify-between font-normal"
        >
          <span className="truncate">
            {value ? selectedLabel || `User #${value}` : 'Select user...'}
          </span>
          {value ? (
            <X
              className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            />
          ) : (
            <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search users..." value={search} onValueChange={setSearch} />
          <CommandList>
            {isLoadingUsers ? (
              <div className="py-3 px-2 text-center text-sm text-muted-foreground">
                Searching...
              </div>
            ) : (
              <>
                <CommandEmpty className="py-3 px-2 text-center text-sm text-muted-foreground">
                  No users found.
                </CommandEmpty>
                <CommandGroup>
                  {users.map((user) => {
                    const fullName = [user.firstName, user.secondName].filter(Boolean).join(' ');
                    const label = fullName ? `${fullName} (#${user.id})` : `User #${user.id}`;
                    return (
                      <CommandItem
                        key={user.id}
                        value={`user-${user.id}`}
                        onSelect={() => handleSelect(user.id, label)}
                      >
                        <Check
                          className={`mr-2 h-4 w-4 ${value === user.id ? 'opacity-100' : 'opacity-0'}`}
                        />
                        {label}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
